/** client/src/components/FieldNote.js **/

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquarePlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function FieldNote({ entityType, entityId, fieldKey, label, preloadedCount, onCountChange }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [count, setCount] = useState(preloadedCount ?? 0);
  const [newNote, setNewNote] = useState('');
  const [newComment, setNewComment] = useState({});
  const [loading, setLoading] = useState(false);
  const [commenting, setCommenting] = useState({});
  const panelRef = useRef(null);

  // Sync preloadedCount from parent when it changes (e.g. doc switch)
  useEffect(() => {
    if (preloadedCount !== undefined) setCount(preloadedCount);
  }, [preloadedCount, entityId]);

  useEffect(() => {
    if (open) loadNotes();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notes`, {
        params: { entity_type: entityType, entity_id: entityId, field_key: fieldKey },
        withCredentials: true,
      });
      setNotes(res.data);
      setCount(res.data.length);
      onCountChange?.(fieldKey, res.data.length);
    } catch (err) {
      console.error('Failed to load notes', err);
    } finally {
      setLoading(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/notes`, {
        entity_type: entityType,
        entity_id: entityId,
        field_key: fieldKey,
        note: newNote.trim(),
      }, { withCredentials: true });
      const updated = [res.data, ...notes];
      setNotes(updated);
      setCount(updated.length);
      onCountChange?.(fieldKey, updated.length);
      setNewNote('');
    } catch (err) {
      console.error('Failed to save note', err);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/api/notes/${noteId}`, { withCredentials: true });
      const updated = notes.filter(n => n.id !== noteId);
      setNotes(updated);
      setCount(updated.length);
      onCountChange?.(fieldKey, updated.length);
    } catch (err) {
      console.error('Failed to delete note', err);
    }
  };

  const submitComment = async (noteId) => {
    const body = newComment[noteId]?.trim();
    if (!body) return;
    setCommenting(prev => ({ ...prev, [noteId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/notes/${noteId}/comments`, { body }, { withCredentials: true });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, comments: res.data } : n));
      setNewComment(prev => ({ ...prev, [noteId]: '' }));
    } catch (err) {
      console.error('Failed to add comment', err);
    } finally {
      setCommenting(prev => ({ ...prev, [noteId]: false }));
    }
  };

  const hasNotes = count > 0;

  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title={`Comments on ${label}`}
        className={`inline-flex items-center gap-0.5 text-xs px-1 py-0.5 rounded transition-colors ${
          hasNotes
            ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
            : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
        }`}
      >
        <MessageSquarePlus className="w-3.5 h-3.5" />
        {hasNotes && <span>{count}</span>}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute z-50 left-0 top-6 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-600">Comments on <em>{label}</em></span>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
          </div>

          <form onSubmit={submitNote} className="flex gap-1 mb-3">
            <input
              autoFocus
              type="text"
              placeholder="Add a comment…"
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              className="flex-1 border rounded px-2 py-1 text-xs"
            />
            <button type="submit" className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700">
              Save
            </button>
          </form>

          {loading && <p className="text-xs text-gray-400">Loading…</p>}

          <div className="space-y-3 max-h-72 overflow-y-auto">
            {notes.map(note => (
              <div key={note.id} className="border rounded p-2 bg-gray-50">
                <div className="flex justify-between items-start">
                  <p className="text-xs text-gray-800 flex-1">{note.note}</p>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="text-gray-300 hover:text-red-500 ml-2 text-xs flex-shrink-0"
                    title="Delete comment"
                  >✕</button>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">{note.author_email} · {new Date(note.created_at).toLocaleString()}</p>

                {note.comments.length > 0 && (
                  <div className="mt-2 space-y-1 pl-2 border-l-2 border-blue-200">
                    {note.comments.map(c => (
                      <div key={c.id} className={`text-xs p-1 rounded ${c.author_type === 'ai' ? 'bg-blue-50 text-blue-800' : 'bg-white text-gray-700'}`}>
                        <span className="font-semibold text-[10px] mr-1">{c.author_type === 'ai' ? '🤖 Atlas AI' : `👤 ${c.author_label}`}</span>
                        {c.body}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-1 mt-2">
                  <input
                    type="text"
                    placeholder="Reply…"
                    value={newComment[note.id] || ''}
                    onChange={e => setNewComment(prev => ({ ...prev, [note.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitComment(note.id); } }}
                    className="flex-1 border rounded px-2 py-0.5 text-[11px]"
                  />
                  <button
                    disabled={commenting[note.id]}
                    onClick={() => submitComment(note.id)}
                    className="bg-gray-200 hover:bg-gray-300 px-2 py-0.5 rounded text-[11px] disabled:opacity-50"
                  >
                    {commenting[note.id] ? '…' : 'Send'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}
